import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { IconButton } from 'utoniq-ui'

import { BaseLayout } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { ResaleItem } from '@/components/organisms'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { getHelpUrl } from '@/lib/helpUrl'

export * from './fragments'

export type State = {
  purchaseFee: number
}

export type Action = {
  onSubmit: () => Promise<void>
}

export const ResaleApplicationCreatePrecautionTemplate: Template<
  State,
  Action
> = ({ state, onSubmit }) => {
  const { t } = useTranslation('resaleApplicationCreatePrecaution')
  const { onClick: onClickHelp } = useBrowserLink(getHelpUrl('resaleApply'))
  const { routerPages, router, generateQueryPath } = useRouter<{
    itemId: string
    skuId?: string
  }>()

  return (
    <>
      <BaseLayout
        margin
        header={{
          back: true,
          defaultBackPage: generateQueryPath(
            routerPages.ResaleApplicationDetail,
            { itemId: router.query.itemId },
            { skuId: router.query.skuId ?? '' }
          ),
          title: t('title', 'リセール応募'),
          right: (
            <IconButton.Help onClick={onClickHelp} edge="end" color="primary" />
          ),
        }}
      >
        <Typography variant="h4" sx={{ py: 5 }}>
          {t('resaleInfo', '応募の際の注意事項')}
        </Typography>
        <NoteCell />
        <ResaleApplicationContentCell purchaseFee={state.purchaseFee} />
        <SubmitButtonCell onSubmit={onSubmit} />
      </BaseLayout>
    </>
  )
}

const NoteCell: Cell = () => {
  const { t } = useTranslation('resaleApplicationCreatePrecaution')
  return (
    <CellFrame
      sx={{
        mb: 5,
        borderColor: 'primary.main',
        borderWidth: 1,
        borderStyle: 'solid',
        p: 2,
      }}
    >
      <Typography variant="body2" color="primary">
        {t('resaleNote')}
      </Typography>
    </CellFrame>
  )
}

type ResaleApplicationContentCellProps = {
  purchaseFee: number
}
const ResaleApplicationContentCell: Cell<ResaleApplicationContentCellProps> = ({
  purchaseFee,
}) => {
  const { t } = useTranslation('resaleApplicationCreatePrecaution')
  return (
    <CellFrame>
      <ResaleItem title={t('purchaseFee', '購入にかかる手数料')}>
        <Stack spacing={1}>
          <Typography variant="body2">
            {t('purchaseFeeDesc', { purchaseFee })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('purchaseFeeNote')}
          </Typography>
        </Stack>
      </ResaleItem>
      <ResaleItem title={t('ticketPayment', 'リセール代金のお支払い')}>
        <Stack spacing={1}>
          <Typography variant="body2">{t('ticketPaymentDesc')}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('ticketPaymentNote')}
          </Typography>
          <Typography variant="body2" color="primary">
            {t(
              'ticketPaymentNotice',
              '支払い期限は翌日の23:59となりますのでご注意ください。'
            )}
          </Typography>
        </Stack>
      </ResaleItem>
      <ResaleItem title={t('cancelApplication')}>
        <Stack>
          <List dense sx={{ listStyleType: 'disc', pl: 5 }}>
            <ListItem disableGutters sx={{ display: 'list-item' }}>
              <Typography variant="body2">
                {t('cancelApplicationDesc1')}
              </Typography>
            </ListItem>
            <ListItem disableGutters sx={{ display: 'list-item' }}>
              <Typography variant="body2">
                {t('cancelApplicationDesc2')}
              </Typography>
            </ListItem>
            <ListItem disableGutters sx={{ display: 'list-item' }}>
              <Typography variant="body2">
                {t('cancelApplicationDesc3')}
              </Typography>
            </ListItem>
          </List>

          <Typography variant="caption" color="text.secondary">
            {t('cancelApplicationNote')}
          </Typography>
        </Stack>
      </ResaleItem>
    </CellFrame>
  )
}

const SubmitButtonCell: Cell<{
  onSubmit: Action['onSubmit']
}> = ({ onSubmit }) => {
  const { t } = useTranslation('resaleApplicationCreatePrecaution')
  const [checked, setChecked] = useState<boolean>(false)
  const { router, routerPages, generatePath } = useRouter<{
    itemId: string
  }>()

  return (
    <CellFrame>
      <Stack spacing={4} alignItems="center" sx={{ pt: 2, pb: 8 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onClick={() => setChecked(prevState => !prevState)}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              {t('checkBoxLabel')}
            </Typography>
          }
        />
        <Button
          size="large"
          disabled={!checked}
          sx={{ width: 212 }}
          onClick={onSubmit}
        >
          {t('submit')}
        </Button>
        <Box />
        <Button
          variant="text"
          size="small"
          sx={{ width: 212 }}
          onClick={() =>
            router.goBack(
              generatePath(routerPages.ResaleApplicationDetail, {
                itemId: router.query.itemId,
              })
            )
          }
        >
          {t('back')}
        </Button>
      </Stack>
    </CellFrame>
  )
}
